import {
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { isDeltekUserorAdmin } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { AllUsersResponse } from 'Models/user'
import {
    AddProductDetailNote,
    DeleteProductDetailNote,
    UpdateProductDetailNote,
} from 'api/graphQL/mutations/productDetailNotes.mutation'
import { GetProductDetailNotes } from 'api/graphQL/queries/productDetailNotes.queries'
import { GetAllUsersQuery } from 'api/graphQL/queries/user.queries'
import { setUsers } from 'api/graphQL/reactiveVars/usersReactiveVar'
import { format } from 'date-fns'
import { MONTH_DATE_YEAR_FORMAT } from 'pages/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useWindow from 'shared/hooks/useWindow'
import {
    addNoteData,
    pdnReactiveVar,
    reasonsReactiveVar,
    removeNoteData,
    removeTempNote,
    setCurrentUser,
    setListingId,
    setNotes,
    setPDNUserList,
    setTempNotes,
    updateNoteData,
    usersReactiveVar,
} from '../PDNStore'
import { PDNResponseType } from '../PDNTypes'
import usePDNReasonsHook from './usePDNReasonsHook'

const DEFAULT_PDN_SEARCH_INPUT = {
    dateFrom: format(new Date('1970 / 01 / 01'), MONTH_DATE_YEAR_FORMAT),
    dateTo: format(new Date(), MONTH_DATE_YEAR_FORMAT),
    listingId: '',
    userIds: [],
    skip: 0,
    take: 1000,
}

const usePDNhook = () => {
    // ReactiveVar States
    const {
        tempNotes,
        listingId,
        currentUser,
        notes,
        count: apiCount,
    } = useReactiveVar(pdnReactiveVar)
    const { initializeReasons } = usePDNReasonsHook()

    const reasons = reasonsReactiveVar()
    const users = usersReactiveVar()
    const location = useLocation()
    const { getWindowURL, getWindowQuery } = useWindow()

    // States
    const [editableNote, setEditableNote] = useState<string | undefined>(
        undefined
    )
    const [isPDNModalOpen, setIsPDNModalOpen] = useState(false)

    // API Queries

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )

    const [
        getProductDetailNotes,
        { data: pdnAPIData, loading: pdnLoading },
    ] = useLazyQuery(GetProductDetailNotes, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            const users = [
                ...new Set(
                    pdnAPIData?.getProductDetailNotes?.result.map(
                        (note: PDNResponseType) => note.metadata.creatorId ?? ''
                    ) as string[]
                ),
            ]

            setPDNUserList(users)
            setNotes(data.getProductDetailNotes, getWindowQuery())
        },
    })

    useQuery<AllUsersResponse>(GetAllUsersQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        onCompleted: (data) => {
            setUsers(data?.getAllUsers?.users)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when fetching notes.',
                    variant: 'error',
                    id: 'Fetch_Notes_Failed',
                },
            })
        },
    })

    const [addNote] = useMutation(AddProductDetailNote, {
        onCompleted: (data) => {
            if (
                data.addProductDetailNote &&
                location.pathname.includes(data.addProductDetailNote.listingId)
            ) {
                addNoteData(data.addProductDetailNote)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when adding note.',
                    variant: 'error',
                    id: 'Add_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const [updateNote] = useMutation(UpdateProductDetailNote, {
        onCompleted: (data) => {
            if (Object.keys(data.updateProductDetailNote).length) {
                updateNoteData(data.updateProductDetailNote)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when updating note.',
                    variant: 'error',
                    id: 'Update_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const [deleteNote] = useMutation(DeleteProductDetailNote, {
        onCompleted: (data) => {
            if (data.deleteProductDetailNote.isDeleted) {
                removeNoteData(data.deleteProductDetailNote.id)
            }
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'An error occurred when deleting note.',
                    variant: 'error',
                    id: 'Delete_Note_Failed',
                },
            })
        },
        fetchPolicy: 'no-cache',
    })

    const { isDeltekUser } = isDeltekUserorAdmin(currentUserInfoResponse)

    const { pdnData } = useMemo(() => {
        const pdnData = [...Object.values(tempNotes), ...Object.values(notes)]
        return { pdnData: pdnLoading ? [] : pdnData }
    }, [tempNotes, notes, pdnLoading])

    const handleSaveNote = useCallback(
        ({
            noteType,
            longDescription,
            isEdit,
            isTemp,
            noteId,
            reason,
            overrideListingId,
        }) => {
            const tempListingId =
                overrideListingId ||
                listingId ||
                location.pathname.split('/product/listing/')[1].split('?')[0]

            if (!!tempListingId) {
                if (!isEdit) {
                    if (
                        [
                            'GENERALNOTE',
                            'COPYMOVE',
                            'DELETEORSTATUSCHANGE',
                        ].includes(noteType) &&
                        !isTemp
                    ) {
                        addNote({
                            variables: {
                                addProductDetailNoteInput: {
                                    content: longDescription,
                                    listingId: tempListingId,
                                    productDetailNoteType: noteType,
                                    reason,
                                },
                            },
                        })
                        return
                    }

                    setTempNotes({ noteType, longDescription, reason })
                } else {
                    if (!isTemp) {
                        updateNote({
                            variables: {
                                productDetailNoteId: noteId,
                                updateProductDetailNoteInput: {
                                    content: longDescription,
                                    listingId: tempListingId,
                                    productDetailNoteType: noteType,
                                    reason,
                                },
                            },
                        })
                    } else {
                        setTempNotes({ noteType, longDescription, reason })
                    }

                    setEditableNote(undefined)
                }
            }
        },
        [editableNote, listingId, location.pathname]
    )

    const handleCancelEditNote = useCallback(() => {
        setEditableNote(undefined)
    }, [])

    const handleEditNote = useCallback(
        (id: string) => {
            setEditableNote(id)
        },
        [setEditableNote]
    )
    const handleDeleteNote = useCallback(
        ({ id, isTemporary }: { id: string; isTemporary: boolean }) => {
            if (!!isTemporary) {
                removeTempNote(id)
                return
            } else {
                deleteNote({
                    variables: {
                        productDetailNoteId: id,
                    },
                })
            }
        },
        [removeTempNote, deleteNote]
    )

    const refetchNotes = useCallback(() => {
        const updatedListingId = getWindowURL()
            .split('product/listing/')[1]
            .split('?')[0]

        getProductDetailNotes({
            variables: {
                input: {
                    listingId: updatedListingId,
                    take: 1000,
                },
            },
        })

        setListingId(updatedListingId)
    }, [getWindowURL, getProductDetailNotes, setListingId])

    useEffect(() => {
        // Set user if not yet set
        if (!!currentUserInfoResponse && !currentUser.fullName) {
            setCurrentUser(currentUserInfoResponse.currentUserInfo)
        }
    }, [currentUserInfoResponse, currentUser])

    return {
        currentUser,
        editableNote,
        handleEditNote,
        handleCancelEditNote,
        handleDeleteNote,
        handleSaveNote,
        initializeReasons,
        isDeltekUser,
        isPDNModalOpen,
        listingId,
        PDNUserList: users,
        pdnData,
        pdnLoading,
        reasons,
        refetchNotes,
        setIsPDNModalOpen,
        tempNotes,
        totalCount: Number(apiCount) + Number(Object.keys(tempNotes).length),
    }
}

export default usePDNhook
